<section
  class="dashboard-page"
  [class.wa-sidebar-expanded]="waUi.sidebarExpanded()"
>
  <header class="page-header">
    <nav class="wa-breadcrumb">
      <a routerLink="/" queryParamsHandling="preserve">Web Analytics</a>
      <span class="sep">/</span>
      <span class="here">Overview</span>
    </nav>
    <h1>Web Analytics</h1>
    @if (activeFilters(); as f) {
      @if (
        f.filterEventType ||
        f.filterUsername ||
        f.filterCountry ||
        f.filterCity ||
        f.filterBrowser ||
        f.filterOs ||
        f.filterDevice ||
        f.filterPath
      ) {
        <p class="dim-filters">
          <span class="label">Slices:</span>
          @if (f.filterEventType) {
            <p-tag [value]="'type:' + f.filterEventType" severity="secondary" />
          }
          @if (f.filterPath) {
            <p-tag [value]="'path:' + f.filterPath" severity="secondary" />
          }
          @if (f.filterUsername) {
            <p-tag [value]="'user:' + f.filterUsername" severity="secondary" />
          }
          @if (f.filterCountry) {
            <p-tag severity="secondary">
              <span class="filter-tag-label">
                country:
                <app-country-label [country]="f.filterCountry" />
              </span>
            </p-tag>
          }
          @if (f.filterCity) {
            <p-tag [value]="'city:' + f.filterCity" severity="secondary" />
          }
          @if (f.filterBrowser) {
            <p-tag [value]="'browser:' + f.filterBrowser" severity="secondary" />
          }
          @if (f.filterOs) {
            <p-tag [value]="'os:' + f.filterOs" severity="secondary" />
          }
          @if (f.filterDevice) {
            <p-tag [value]="'device:' + f.filterDevice" severity="secondary" />
          }
        </p>
      }
    }
  </header>

  @if (loading()) {
    <div class="skeleton-block">
      <p-skeleton height="5rem" styleClass="wa-sk mb-3" />
      <p-skeleton height="18rem" styleClass="wa-sk mb-3" />
      <p-skeleton height="14rem" styleClass="wa-sk" />
    </div>
  } @else if (error(); as err) {
      <p-message severity="error" [text]="err" />
    } @else if (dashboard(); as d) {
      <div class="view-toolbar">
        @for (v of views; track v.id) {
          <p-button
            type="button"
            [label]="v.label"
            [outlined]="activeView() !== v.id"
            size="small"
            (onClick)="setView(v.id)"
          />
        }
        <p-button
          type="button"
          label="Users"
          [outlined]="true"
          size="small"
          icon="pi pi-users"
          routerLink="/users"
          queryParamsHandling="preserve"
        />
      </div>

      @switch (activeView()) {
        @case ('overview') {
          <div class="live-stat-row">
            <p-card styleClass="wa-stat-card wa-stat-card--live">
              <div class="stat-inner">
                <div class="live-head">
                  <span class="live-dot" aria-hidden="true"></span>
                  <span class="stat-label">Active now (5 min)</span>
                </div>
                @if (activeNowLoading() && !activeNow()) {
                  <span class="stat-value dim">—</span>
                } @else if (activeNowError(); as err) {
                  <span class="stat-value dim">{{ err }}</span>
                } @else if (activeNow(); as live) {
                  <span class="stat-value">{{ live.activeSessions | number }}</span>
                  <span class="stat-sub">
                    {{ live.activeNamedUsers | number }} named · {{ activeNowScopeLabel() }}
                  </span>
                  <span class="stat-sub dim">Updated {{ live.asOf | date: 'HH:mm:ss' }} UTC</span>
                } @else {
                  <span class="stat-value dim">—</span>
                }
              </div>
            </p-card>
          </div>

          <div class="stat-row stat-row--5">
            <p-card styleClass="wa-stat-card wa-stat-card--spark">
              <div class="stat-inner">
                <span class="stat-label">Total events</span>
                <span class="stat-value">{{ d.totalEvents | number }}</span>
                @if (kpiDelta()['total']; as dt) {
                  <span
                    class="stat-delta"
                    [class.positive]="dt.startsWith('+')"
                    [class.negative]="dt.startsWith('-')"
                    >{{ dt }} vs prev</span
                  >
                }
                @if (sparkTotal(); as sp) {
                  <div class="spark-wrap">
                    <p-chart type="line" [data]="sp.data" [options]="sp.options" />
                  </div>
                }
              </div>
            </p-card>
            <p-card styleClass="wa-stat-card wa-stat-card--spark">
              <div class="stat-inner">
                <span class="stat-label">Page views</span>
                <span class="stat-value">{{ d.pageViews | number }}</span>
                @if (kpiDelta()['page']; as dt) {
                  <span
                    class="stat-delta"
                    [class.positive]="dt.startsWith('+')"
                    [class.negative]="dt.startsWith('-')"
                    >{{ dt }} vs prev</span
                  >
                }
                @if (sparkPage(); as sp) {
                  <div class="spark-wrap">
                    <p-chart type="line" [data]="sp.data" [options]="sp.options" />
                  </div>
                }
              </div>
            </p-card>
            <p-card styleClass="wa-stat-card wa-stat-card--spark">
              <div class="stat-inner">
                <span class="stat-label">API requests</span>
                <span class="stat-value">{{ d.apiRequests | number }}</span>
                @if (kpiDelta()['api']; as dt) {
                  <span
                    class="stat-delta"
                    [class.positive]="dt.startsWith('+')"
                    [class.negative]="dt.startsWith('-')"
                    >{{ dt }} vs prev</span
                  >
                }
                @if (sparkApi(); as sp) {
                  <div class="spark-wrap">
                    <p-chart type="line" [data]="sp.data" [options]="sp.options" />
                  </div>
                }
              </div>
            </p-card>
            <p-card styleClass="wa-stat-card wa-stat-card--spark">
              <div class="stat-inner">
                <span class="stat-label">Sessions</span>
                <span class="stat-value">{{ d.uniqueSessions | number }}</span>
                @if (kpiDelta()['sess']; as dt) {
                  <span
                    class="stat-delta"
                    [class.positive]="dt.startsWith('+')"
                    [class.negative]="dt.startsWith('-')"
                    >{{ dt }} vs prev</span
                  >
                }
                @if (sparkSess(); as sp) {
                  <div class="spark-wrap">
                    <p-chart type="line" [data]="sp.data" [options]="sp.options" />
                  </div>
                }
              </div>
            </p-card>
            <p-card
              styleClass="wa-stat-card wa-stat-card--spark wa-stat-card--click"
              (click)="goUsers()"
              (keyup.enter)="goUsers()"
              tabindex="0"
              role="button"
            >
              <div class="stat-inner">
                <span class="stat-label"
                  >Unique users (named)
                  <i class="pi pi-external-link wa-mini-link" aria-hidden="true"></i
                ></span>
                <span class="stat-value">{{ d.uniqueUsernames | number }}</span>
                @if (kpiDelta()['users']; as dt) {
                  <span
                    class="stat-delta"
                    [class.positive]="dt.startsWith('+')"
                    [class.negative]="dt.startsWith('-')"
                    >{{ dt }} vs prev</span
                  >
                }
                @if (sparkUsers(); as sp) {
                  <div class="spark-wrap">
                    <p-chart type="line" [data]="sp.data" [options]="sp.options" />
                  </div>
                }
              </div>
            </p-card>
          </div>

          <p-card styleClass="wa-chart-card">
            <ng-template pTemplate="title">
              <div class="chart-card-head">
                <span>Activity over time</span>
                <div class="chart-toolbar">
                  <p-button
                    size="small"
                    [outlined]="activityMode() !== 'stacked'"
                    label="Stacked"
                    (onClick)="setActivityMode('stacked')"
                  />
                  <p-button
                    size="small"
                    [outlined]="activityMode() !== 'lines'"
                    label="Lines"
                    (onClick)="setActivityMode('lines')"
                  />
                  <p-button
                    size="small"
                    [outlined]="activityMode() !== 'total'"
                    label="Total"
                    (onClick)="setActivityMode('total')"
                  />
                  <span class="sep-v"></span>
                  <p-button
                    size="small"
                    [outlined]="activityGranularity() !== 'daily'"
                    label="Daily"
                    (onClick)="setGranularity('daily')"
                  />
                  <p-button
                    size="small"
                    [outlined]="activityGranularity() !== 'weekly'"
                    label="Weekly"
                    (onClick)="setGranularity('weekly')"
                  />
                  <p-button
                    size="small"
                    [outlined]="activityGranularity() !== 'hourly'"
                    label="Hourly"
                    (onClick)="setGranularity('hourly')"
                    pTooltip="Uses daily buckets (hourly not stored)"
                    tooltipPosition="top"
                  />
                </div>
              </div>
            </ng-template>
            @if (activityGranularity() === 'hourly') {
              <p class="table-note">Hourly view uses the same daily resolution as the API.</p>
            }
            @if (activityMainData(); as actData) {
              <div class="chart-box chart-box--tall">
                <p-chart
                  type="line"
                  [data]="actData"
                  [options]="activityMainOptions() ?? {}"
                />
              </div>
            } @else {
              <p class="empty-hint">No activity breakdown for this range</p>
            }
          </p-card>

          <div class="charts-split">
            <p-card styleClass="wa-chart-card">
              <ng-template pTemplate="title">
                <span>Event types</span>
                <span class="subtle-hint">Click a bar to filter</span>
              </ng-template>
              @if (d.eventTypeBreakdown.length && eventTypeHorizData(); as hData) {
                <div class="chart-box chart-box--paths">
                  <p-chart
                    type="bar"
                    [data]="hData"
                    [options]="eventTypeHorizOptions() ?? {}"
                  />
                </div>
              } @else {
                <p class="empty-hint">No events in this range</p>
              }
            </p-card>
            <p-card header="Top pages" styleClass="wa-chart-card">
              @if (d.topPaths.length && pathsChartData(); as pathData) {
                <div class="chart-box chart-box--paths">
                  <p-chart
                    type="bar"
                    [data]="pathData"
                    [options]="pathsChartOptions() ?? {}"
                  />
                </div>
              } @else {
                <p class="empty-hint">No path data</p>
              }
            </p-card>
          </div>

          <p-card header="Top pages (detail)" styleClass="wa-table-card">
            <p-table
              [value]="topPagesWithLabels(d)"
              [rows]="10"
              [paginator]="topPagesWithLabels(d).length > 10"
              [showCurrentPageReport]="true"
              currentPageReportTemplate="Showing {first}–{last} of {totalRecords}"
              styleClass="wa-paths-table"
            >
              <ng-template pTemplate="header">
                <tr>
                  <th>Page</th>
                  <th class="col-bar">Share</th>
                  <th class="col-count">Count</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr
                  class="path-row-click"
                  (click)="navigateFilterPath(row.path)"
                  (keyup.enter)="navigateFilterPath(row.path)"
                  tabindex="0"
                >
                  <td class="path-cell">
                    <span class="page-title">{{ row.label }}</span>
                    <span
                      class="path-raw muted"
                      [pTooltip]="row.path"
                      tooltipPosition="top"
                      >{{ truncatePathUi(row.path, 56) }}</span
                    >
                  </td>
                  <td class="col-bar">
                    <div class="pct-bar-track">
                      <div
                        class="pct-bar-fill"
                        [style.width.%]="row.pct"
                      ></div>
                    </div>
                    <span class="pct-label">{{ row.pct | number: '1.1-1' }}%</span>
                  </td>
                  <td class="col-count">{{ row.count | number }}</td>
                </tr>
              </ng-template>
              <ng-template pTemplate="emptymessage">
                <tr>
                  <td colspan="3">No rows</td>
                </tr>
              </ng-template>
            </p-table>
          </p-card>

          <p-card header="Session flows (top page_view sequences)" styleClass="wa-chart-card">
            @if (d.topNavigationFlows?.length) {
              <ul class="flow-list">
                @for (flow of d.topNavigationFlows; track $index) {
                  <li>
                    <span class="flow-label">{{ flowDisplayLabel(flow) }}</span>
                    <span class="flow-count">{{ flow.count | number }} sessions</span>
                  </li>
                }
              </ul>
            } @else {
              <p class="empty-hint">No multi-step flows in this range</p>
            }
          </p-card>
        }
        @case ('audience') {
          <div class="stat-row stat-row--3">
            <p-card styleClass="wa-stat-card">
              <div class="stat-inner">
                <span class="stat-label">Distinct users</span>
                <span class="stat-value">{{ d.uniqueUsernames | number }}</span>
              </div>
            </p-card>
            <p-card styleClass="wa-stat-card">
              <div class="stat-inner">
                <span class="stat-label">Sessions</span>
                <span class="stat-value">{{ d.uniqueSessions | number }}</span>
              </div>
            </p-card>
            <p-card styleClass="wa-stat-card">
              <div class="stat-inner">
                <span class="stat-label">Countries (rows)</span>
                <span class="stat-value">{{ d.topCountries.length | number }}</span>
              </div>
            </p-card>
          </div>
          <div class="charts-split">
            <p-card header="Countries" styleClass="wa-chart-card">
              @if (d.topCountries.length && countryChartData(); as cData) {
                <div class="chart-box chart-box--donut">
                  <p-chart
                    type="doughnut"
                    [data]="cData"
                    [options]="countryChartOptions() ?? {}"
                  />
                </div>
              } @else {
                <p class="empty-hint">No country data (needs CloudFront geo headers)</p>
              }
            </p-card>
            <p-card header="Cities" styleClass="wa-chart-card">
              @if (d.topCities.length && cityBarChartData(); as cityData) {
                <div class="chart-box chart-box--paths">
                  <p-chart
                    type="bar"
                    [data]="cityData"
                    [options]="cityBarChartOptions() ?? {}"
                  />
                </div>
              } @else {
                <p class="empty-hint">No city data</p>
              }
            </p-card>
          </div>
          <p-card header="Top usernames" styleClass="wa-chart-card">
            @if (d.topUsernames.length && usernameBarChartData(); as uData) {
              <div class="chart-box chart-box--paths">
                <p-chart
                  type="bar"
                  [data]="uData"
                  [options]="usernameBarChartOptions() ?? {}"
                />
              </div>
            } @else {
              <p class="empty-hint">No usernames in this slice</p>
            }
          </p-card>
          <p-card header="Source IPs (top)" styleClass="wa-table-card">
            <p class="table-note">
              IPs are taken from the ingest request when X-Forwarded-For / CloudFront viewer
              headers are available.
            </p>
            <p-table [value]="d.topViewerAddresses" [rows]="15" styleClass="wa-paths-table">
              <ng-template pTemplate="header">
                <tr>
                  <th>Address</th>
                  <th class="col-count">Events</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td class="mono-cell">{{ row.label }}</td>
                  <td class="col-count">{{ row.count | number }}</td>
                </tr>
              </ng-template>
              <ng-template pTemplate="emptymessage">
                <tr>
                  <td colspan="2">No IP data</td>
                </tr>
              </ng-template>
            </p-table>
          </p-card>
        }
        @case ('technology') {
          <div class="charts-split">
            <p-card header="Browsers" styleClass="wa-chart-card">
              @if (d.topBrowsers.length && browserChartData(); as bData) {
                <div class="chart-box chart-box--donut">
                  <p-chart
                    type="doughnut"
                    [data]="bData"
                    [options]="browserChartOptions() ?? {}"
                  />
                </div>
              } @else {
                <p class="empty-hint">No browser data</p>
              }
            </p-card>
            <p-card header="Operating systems" styleClass="wa-chart-card">
              @if (d.topOs.length && osBarChartData(); as osData) {
                <div class="chart-box chart-box--paths">
                  <p-chart
                    type="bar"
                    [data]="osData"
                    [options]="osBarChartOptions() ?? {}"
                  />
                </div>
              } @else {
                <p class="empty-hint">No OS data</p>
              }
            </p-card>
          </div>
          <p-card header="Devices" styleClass="wa-chart-card">
            @if (d.topDevices.length && deviceBarChartData(); as devData) {
              <div class="chart-box chart-box--mid">
                <p-chart
                  type="bar"
                  [data]="devData"
                  [options]="deviceBarChartOptions() ?? {}"
                />
              </div>
            } @else {
              <p class="empty-hint">No device data</p>
            }
          </p-card>
        }
        @case ('performance') {
          <div class="stat-row stat-row--4">
            <p-card styleClass="wa-stat-card">
              <div class="stat-inner">
                <span class="stat-label">Performance events</span>
                <span class="stat-value">{{ (d.performanceEventCount ?? 0) | number }}</span>
              </div>
            </p-card>
            <p-card styleClass="wa-stat-card">
              <div class="stat-inner">
                <span class="stat-label">Avg LCP</span>
                <span class="stat-value">{{ performanceRangeAvg(d, 'avgLcp') }}</span>
              </div>
            </p-card>
            <p-card styleClass="wa-stat-card">
              <div class="stat-inner">
                <span class="stat-label">Avg CLS</span>
                <span class="stat-value">{{ performanceRangeAvg(d, 'avgCls') }}</span>
              </div>
            </p-card>
            <p-card styleClass="wa-stat-card">
              <div class="stat-inner">
                <span class="stat-label">Samples with metrics</span>
                <span class="stat-value">{{ performanceSampleCount(d) | number }}</span>
              </div>
            </p-card>
          </div>
          <p-card header="Web Vitals trend (daily averages)" styleClass="wa-chart-card">
            @if (d.performanceByDay?.length && performanceChartData(); as perfData) {
              <div class="chart-box chart-box--mid">
                <p-chart
                  type="line"
                  [data]="perfData"
                  [options]="performanceChartOptions() ?? {}"
                />
              </div>
            } @else {
              <p class="empty-hint">
                No parseable performance metrics in rollups for this range. Run backfill after deploy if you have historical raw events.
              </p>
            }
          </p-card>
          <p-card
            header="Slowest paths by average LCP"
            styleClass="wa-table-card wa-mt"
          >
            <p class="table-note">
              Aggregated from performance event extra JSON (LCP, CLS, FCP, TTFB). User detail still shows raw per-event values for debugging.
            </p>
            <p-table
              [value]="d.topPathsByLcp ?? []"
              [rows]="15"
              [paginator]="(d.topPathsByLcp ?? []).length > 15"
              [showCurrentPageReport]="true"
              currentPageReportTemplate="Showing {first}–{last} of {totalRecords}"
            >
              <ng-template pTemplate="header">
                <tr>
                  <th>Path</th>
                  <th class="col-count">Samples</th>
                  <th class="col-count">Avg LCP</th>
                  <th class="col-count">Avg CLS</th>
                  <th class="col-count">Avg FCP</th>
                  <th class="col-count">Avg TTFB</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td [pTooltip]="row.path" tooltipPosition="top">
                    <span class="mono-cell">{{ performancePathLabel(row.path) }}</span>
                  </td>
                  <td class="col-count">{{ row.count | number }}</td>
                  <td class="col-count">{{ formatPerformanceMetric('avgLcp', row.avgLcp) }}</td>
                  <td class="col-count">{{ formatPerformanceMetric('avgCls', row.avgCls) }}</td>
                  <td class="col-count">{{ formatPerformanceMetric('avgFcp', row.avgFcp) }}</td>
                  <td class="col-count">{{ formatPerformanceMetric('avgTtfb', row.avgTtfb) }}</td>
                </tr>
              </ng-template>
              <ng-template pTemplate="emptymessage">
                <tr>
                  <td colspan="6">No performance samples with LCP in this slice.</td>
                </tr>
              </ng-template>
            </p-table>
          </p-card>
        }
        @case ('api') {
          <div class="stat-row stat-row--3">
            <p-card styleClass="wa-stat-card">
              <div class="stat-inner">
                <span class="stat-label">API request events</span>
                <span class="stat-value">{{ d.apiRequests | number }}</span>
              </div>
            </p-card>
            <p-card styleClass="wa-stat-card">
              <div class="stat-inner">
                <span class="stat-label">Total events</span>
                <span class="stat-value">{{ d.totalEvents | number }}</span>
              </div>
            </p-card>
            <p-card styleClass="wa-stat-card">
              <div class="stat-inner">
                <span class="stat-label">Share of traffic</span>
                <span class="stat-value">{{ apiTrafficSharePercent(d) }}%</span>
              </div>
            </p-card>
          </div>
          <p-card
            header="Top GraphQL operations (from api_request extra)"
            styleClass="wa-table-card"
          >
            <p class="table-note">
              Each AppSync query/mutation from integrated apps can emit an api_request event (operationName + operationType in extra JSON). Filter event type with 'api_request' in the sidebar to focus this stream.
            </p>
            <p-table
              [value]="d.topApiOperations ?? []"
              [rows]="15"
              [paginator]="(d.topApiOperations ?? []).length > 15"
              [showCurrentPageReport]="true"
              currentPageReportTemplate="Showing {first}–{last} of {totalRecords}"
            >
              <ng-template pTemplate="header">
                <tr>
                  <th>Operation</th>
                  <th class="col-op-type">Kind</th>
                  <th class="col-count">Count</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td class="mono-cell">{{ row.operationName }}</td>
                  <td class="col-op-type">{{ row.operationType }}</td>
                  <td class="col-count">{{ row.count | number }}</td>
                </tr>
              </ng-template>
              <ng-template pTemplate="emptymessage">
                <tr>
                  <td colspan="3">No api_request rows with parseable extra in this slice.</td>
                </tr>
              </ng-template>
            </p-table>
          </p-card>
          <p-card header="All event types (table)" styleClass="wa-table-card wa-mt">
            <p-table
              [value]="d.eventTypeBreakdown"
              [rows]="12"
              [paginator]="d.eventTypeBreakdown.length > 12"
              [showCurrentPageReport]="true"
              currentPageReportTemplate="Showing {first}–{last} of {totalRecords}"
            >
              <ng-template pTemplate="header">
                <tr>
                  <th>Type</th>
                  <th class="col-count">Count</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.eventType }}</td>
                  <td class="col-count">{{ row.count | number }}</td>
                </tr>
              </ng-template>
            </p-table>
          </p-card>
        }
      }
    } @else {
      <p-message severity="info" text="No dashboard data returned." />
    }
</section>
